import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import ElementIcon from '../ElementIcon';
import { Icon } from 'src/components/ui/Icon';
import AIChatModal from './AIChatModal';
import WindowPortal from './WindowPortal';
import useAIChat from './useAIChat';

/**
 * Floating chat assistant. Rendered through a React portal into <body> so it
 * escapes any parent stacking/overflow context. Can also "pop out" into a
 * SEPARATE browser window (another React portal, WindowPortal) so the user can
 * drag it to a second screen while keeping full chat state.
 */
function AIChatFAV(props) {
  const { t } = useTranslation();
  const [poppedOut, setPoppedOut] = useState(false);

  const {
    agent,
    agentTyping,
    agentError,
    agentStatus,
    messages,
    sendMessage,
    isOpen,
    toggleOpenChat,
    closeChat,
    chatContext,
    clearChat,
  } = useAIChat(props);

  const dock = useCallback(() => setPoppedOut(false), []);
  const closeAll = useCallback(() => { setPoppedOut(false); closeChat(); }, [closeChat]);

  if (typeof document === 'undefined') return null;

  const modalProps = {
    agent,
    messages,
    chatContext,
    agentTyping,
    agentError,
    agentStatus,
    onSend: sendMessage,
    onClear: clearChat,
  };

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[900000]">
      {!isOpen ? (
        <button
          type="button"
          onClick={toggleOpenChat}
          title={`Open ${agent.name ?? ''}`}
          className="pointer-events-auto fixed bottom-5 right-5 flex h-14 w-14 items-center justify-center rounded-full text-xl text-white shadow-xl ring-1 ring-black/10 transition-transform duration-200 hover:scale-105 active:scale-95 theme-accent-bg"
        >
          <ElementIcon element={agent.icon} />
        </button>
      ) : poppedOut ? (
        <>
          {/* Main-window hint while the chat lives in the detached window. */}
          <button
            type="button"
            onClick={dock}
            title={t('Bring the chat back')}
            className="pointer-events-auto fixed bottom-5 right-5 flex items-center gap-2 rounded-full px-4 py-2.5 text-sm text-white shadow-xl ring-1 ring-black/10 theme-accent-bg"
          >
            <Icon name="external-link" />
            {t('Chat is open in another window')}
          </button>
          <WindowPortal title={agent.name} onClose={dock}>
            <AIChatModal {...modalProps} windowMode onDock={dock} onClose={closeAll} />
          </WindowPortal>
        </>
      ) : (
        <AIChatModal {...modalProps} onClose={closeChat} onPopOut={() => setPoppedOut(true)} />
      )}
    </div>,
    document.body
  );
}

export default AIChatFAV;
